import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { updateService } from '../../services/updateService';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ClearSWCacheModal({ isOpen, onClose }: UpdateModalProps) {
  const [isClearing, setIsClearing] = useState(false);
  const [hostUnreachable, setHostUnreachable] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsClearing(false);
      setHostUnreachable(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Clearing the cache reloads the app from its host, and "online" can be true
  // while that host is down, so check the host answers before clearing anything.
  const handleUpdate = async () => {
    setHostUnreachable(false);
    setIsClearing(true);
    if (!(await updateService.isAppHostReachable())) {
      setHostUnreachable(true);
      setIsClearing(false);
      return;
    }
    await updateService.updateToNewVersion();
    // updateToNewVersion schedules the reload itself and logs rather than
    // throws on failure, so re-enable the buttons in case no reload follows.
    setIsClearing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={isClearing ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative mx-4 max-w-md rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-4">
          <h2 className="mb-2 text-xl font-bold text-ink">Clear App Cache</h2>
          <p className="mb-4 text-inkMuted">
            Clear the app cache to apply the latest updates.
          </p>

          <div className="mb-4 border-l-4 border-amber-400 bg-amber-50 p-3">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-amber-700">
                  <strong>Important:</strong> Clearing cache may cause loss of
                  any in-progress inspection data that hasn't been saved.
                </p>
              </div>
            </div>
          </div>

          {hostUnreachable && (
            <div className="mb-4 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
              <WifiOff className="mt-0.5 shrink-0 text-amber-600" size={18} />
              <p className="text-sm text-amber-700">
                <strong>The update server cannot be reached.</strong> Nothing
                was cleared - try again in a few minutes.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isClearing}
            className="rounded-sm bg-line/40 px-4 py-2 text-inkMuted transition-colors hover:bg-line/60 active:bg-line/70 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={isClearing}
            className="rounded-sm bg-brand px-4 py-2 text-white transition-colors hover:bg-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isClearing ? 'Clearing...' : 'Clear Cache Now'}
          </button>
        </div>
      </div>
    </div>
  );
}
